import { Response, httpDelete, httpGet, httpPost } from "@config/http";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { trackPromise } from "react-promise-tracker";

import { GameBodyProps, UpcomingGamesBodyProps } from "./game.type";

const { REACT_APP_BASE_URL } = process.env;
const { REACT_APP_MAPBOX_KEY } = process.env;

const initialState = {};

const createGame = createAsyncThunk(
  "game/Create game",
  async (body: GameBodyProps) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPost(`${REACT_APP_BASE_URL}/api/v1/games/`, body, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getGames = createAsyncThunk(
  "game/get all games for the user by rsvp",
  async (rsvp: string) => {
    let results: Response | null;
    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/game-rsvps/`;
    const urlWithQuery = `${baseUrl}?status=${rsvp}`;
    try {
      results = await trackPromise(httpGet(urlWithQuery, true));
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getGameTransactionFee = createAsyncThunk(
  "get game txn fee",
  async ({
    fee,
    currency,
    game_id,
  }: {
    fee: number;
    currency: string;
    game_id: string | undefined;
  }) => {
    let results: Response | null;
    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/stripe/payment/transaction-fee`;
    let urlWithQuery = `${baseUrl}?game_fee=${fee}&game_currency=${currency}`;
    if (game_id !== undefined){
      urlWithQuery = `${urlWithQuery}&game_id=${game_id}`;
    }
    try {
      results = await trackPromise(httpGet(urlWithQuery, true));
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getLocation = createAsyncThunk(
  "game/get location suggestions",
  async ({
    location,
    sessionToken,
  }: {
    location: string;
    sessionToken: string;
  }) => {
    let results: Response | null;
    const urlWithQuery = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${location}&access_token=${REACT_APP_MAPBOX_KEY}&language=en&session_token=${sessionToken}`;
    try {
      results = await httpGet(urlWithQuery, false);
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const retreiveLocation = createAsyncThunk(
  "game/retreive location ",
  async ({
    mapboxId,
    sessionToken,
  }: {
    mapboxId: string;
    sessionToken: string;
  }) => {
    let results: Response | null;
    const urlWithQuery = `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?access_token=${REACT_APP_MAPBOX_KEY}&session_token=${sessionToken}`;
    try {
      results = await httpGet(urlWithQuery, false);
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const upcomingGames = createAsyncThunk(
  "game/get all upcoming games",
  async (params: UpcomingGamesBodyProps) => {
    let results: Response | null;
    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/game-rsvps/`;
    const urlWithQuery = `${baseUrl}?end_date_after=${params.date}&status=${params.rsvp}`;
    try {
      results = await trackPromise(httpGet(urlWithQuery, true));
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const pastGames = createAsyncThunk(
  "game/get all past Games",
  async (params: UpcomingGamesBodyProps) => {
    let results: Response | null;
    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/game-rsvps/`;
    const urlWithQuery = `${baseUrl}?end_date_before=${params.date}&status=${params.rsvp}`;
    try {
      results = await trackPromise(httpGet(urlWithQuery, true));
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const leaveWaitList = createAsyncThunk(
  "game/leave waitList",
  async (id: string) => {
    let results: Response | null;
    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/waitlist/${id}`;
    try {
      results = await trackPromise(httpDelete(baseUrl, true));
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const waitListGames = createAsyncThunk(
  "game/get waitList games",
  async (date: string) => {
    let results: Response | null;
    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/waitlist/`;
    const urlWithQuery = `${baseUrl}?end_date_after=${date}`;
    try {
      results = await trackPromise(httpGet(urlWithQuery, true));
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

// TODO: Add filters
const marketplaceGames = createAsyncThunk(
  "game/get marketplace games",
  async (body: { address: string; postCode: string; date: string }) => {
    let results: Response | null;
    function getStartAndEndDate(date: string) {
      // Parse the input date string
      const inputDateTime = new Date(date);

      // Set the time to midnight for the start date
      const startDate = new Date(inputDateTime);
      startDate.setUTCHours(0, 0, 0, 0);

      // Set the time to midnight of the next day for the end date
      const endDate = new Date(inputDateTime);
      endDate.setUTCHours(24, 0, 0, 0);

      // Return an object with start and end dates
      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }

    const baseUrl = `${REACT_APP_BASE_URL}/api/v1/games/_search`;
    const queryParams = [];

    if (body.address) {
      queryParams.push(`full_address=${encodeURIComponent(body.address)}`);
    }

    if (body.postCode) {
      queryParams.push(`postcode=${encodeURIComponent(body.postCode)}`);
    }

    if (body.date) {
      queryParams.push(
        `start_date_before=${encodeURIComponent(
          getStartAndEndDate(body?.date).endDate
        )}`
      );
      queryParams.push(
        `start_date_after=${encodeURIComponent(
          getStartAndEndDate(body?.date).startDate
        )}`
      );
    }

    const urlWithQuery = queryParams.length
      ? `${baseUrl}?${queryParams.join("&")}`
      : baseUrl;

    try {
      results = await trackPromise(httpPost(urlWithQuery, null, false));
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const gameSlice = createSlice({
  name: "game",
  initialState: initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {},
});

export const gameReducer = gameSlice.reducer;

export const gameActions = {
  ...gameSlice.actions,
  createGame,
  getGames,
  upcomingGames,
  pastGames,
  getLocation,
  retrieveLocation: retreiveLocation,
  leaveWaitList,
  waitListGames,
  marketplaceGames,
  getGameTransactionFee,
};
